import { useQuery } from '@apollo/client'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
    RosterProfileQuery,
    RosterProfileQueryVariables,
    RosterType,
} from '__generated__/graphql'
import { useNavigate, useParams } from 'react-router-dom'
import { useRegisterPageVisit } from 'recruiter/components/GlobalSearch/hooks/useRegisterPageVisit'
import { GetRosterProfile } from 'recruiter/graphql/rosters.gql'
import { recruiterRouteConfig, type RosterRouteParams } from 'recruiter/routes'
import useToast from 'shared/components/Toast/useToast'
import RosterMappingProfile from './Mappings/RosterMappingProfile'
import RosterSearchProfile from './Search/RosterSearchProfile'

const RosterProfile: React.FC = () => {
    const { rosterId } = useParams<RosterRouteParams>()
    if (!rosterId) {
        throw new Error('Roster ID is required')
    }
    const toast = useToast()
    const navigate = useNavigate()
    const {
        data: rosterProfileData,
        loading,
        refetch,
    } = useQuery<RosterProfileQuery, RosterProfileQueryVariables>(
        GetRosterProfile as TypedDocumentNode<
            RosterProfileQuery,
            RosterProfileQueryVariables
        >,
        {
            variables: { id: rosterId },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: error.message,
                })

                navigate(recruiterRouteConfig.rosters.path)
            },
        }
    )

    const roster = rosterProfileData?.roster
    useRegisterPageVisit({
        type: 'roster',
        id: rosterId,
        name: roster?.rosterName ?? undefined,
        subtitle: roster?.company?.value ?? undefined,
    })
    const isMapping =
        rosterProfileData?.roster?.rosterType === RosterType.Mapping
    return (
        <>
            {isMapping ? (
                <RosterMappingProfile
                    roster={rosterProfileData.roster}
                    loading={loading}
                    refetch={refetch}
                />
            ) : (
                <RosterSearchProfile
                    roster={rosterProfileData?.roster}
                    loading={loading}
                    refetch={refetch}
                />
            )}
        </>
    )
}

export default RosterProfile
